import { FC } from 'react';
import { Calendar as CalendarIcon, Check, Loader2 } from 'lucide-react';
import { useGetDayAvailabilitySlotsQuery, type UserAvailabilitySlot } from '@teachur/http-client';
import {
  FIRST_HOUR,
  END_OF_DAY_HOUR,
  ZERO_MINUTES,
  ZERO_SECONDS,
  ZERO_MILLISECONDS,
  END_OF_DAY_MINUTE,
  END_OF_DAY_SECOND,
  END_OF_DAY_MILLISECOND,
} from '@new/features/schedule/lib/constants';
import { BOOKING_BUFFER_HOURS, SUNDAY, SATURDAY } from '../lib/constants';
import {
  formatSelectedDayTitle,
  formatSlotStartTime,
  addBusinessHours,
  getSlotStatus,
} from '@new/features/schedule/lib/helpers';

interface AvailableSlotsListProps {
  selectedDay: Date;
  courseId: number;
  selectedSlotId?: number | null;
  onSlotSelect?: (slotId: number | null) => void;
}

export const AvailableSlotsList: FC<AvailableSlotsListProps> = ({
  selectedDay,
  courseId,
  selectedSlotId,
  onSlotSelect,
}) => {
  const startOfDay = new Date(selectedDay);
  startOfDay.setHours(FIRST_HOUR, ZERO_MINUTES, ZERO_SECONDS, ZERO_MILLISECONDS);

  const endOfDay = new Date(selectedDay);
  endOfDay.setHours(END_OF_DAY_HOUR, END_OF_DAY_MINUTE, END_OF_DAY_SECOND, END_OF_DAY_MILLISECOND);

  const {
    data: slots,
    isFetching,
    isPreviousData,
  } = useGetDayAvailabilitySlotsQuery({
    startDate: startOfDay.toISOString(),
    endDate: endOfDay.toISOString(),
    courseId,
    availableOnly: true,
  });

  const isShowingStaleDay = isFetching && isPreviousData;

  const earliestBookable = addBusinessHours(new Date(), BOOKING_BUFFER_HOURS);

  const uniqueSlots = slots
    ?.filter((slot) => {
      const d = new Date(slot.start);
      const day = d.getDay();
      return (
        d >= earliestBookable &&
        day !== SUNDAY &&
        day !== SATURDAY &&
        getSlotStatus(slot) === 'available'
      );
    })
    .filter(
      (slot, index, arr) =>
        arr.findIndex((s) => new Date(s.start).getHours() === new Date(slot.start).getHours()) ===
        index
    );

  const handleSlotClick = (slot: UserAvailabilitySlot) => {
    if (isShowingStaleDay) return;
    onSlotSelect?.(slot.id);
  };

  if ((!slots || !slots.length) && !isShowingStaleDay) {
    return (
      <div className="flex-1 flex items-center justify-center p-2">
        <div className="flex flex-col items-center justify-center gap-3 lg:gap-6">
          <CalendarIcon
            className="w-8 lg:w-12 h-8 lg:h-12 text-(--neutral-white-NW500)!"
            strokeWidth={1.5}
          />
          <div className="flex flex-col items-center gap-1">
            <p className="text-(--neutral-black-NB800)! [font-size:var(--fs-l4)]! lg:[font-size:var(--fs-l3)]! font-medium m-0!">
              No available slots for this date.
            </p>
            <p className="text-(--neutral-black-NB700)! [font-size:var(--fs-l5)] lg:[font-size:var(--fs-l4)]! text-center leading-tight m-0!">
              Select another date with highlighted background.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h3 className="[font-size:var(--fs-l4)]! lg:[font-size:var(--fs-l2)]! [font-family:var(--font-montserrat)]! text-(--neutral-black-NB1000)! font-semibold m-0!">
          {formatSelectedDayTitle(selectedDay)}
        </h3>
        {isShowingStaleDay && (
          <span
            role="status"
            aria-label="Loading slots"
            className="flex items-center gap-1 text-(--neutral-black-NB600)! [font-size:var(--fs-l6)]!"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading slots...
          </span>
        )}
      </div>
      <div
        className={`max-h-full md:max-h-72 md:overflow-y-auto gap-2 flex flex-col ${isShowingStaleDay ? 'opacity-50' : ''}`}
      >
        {uniqueSlots?.map((slot) => {
          const isSelected = selectedSlotId === slot.id;
          return (
            <button
              key={slot.id}
              onClick={() => handleSlotClick(slot)}
              disabled={isShowingStaleDay}
              aria-busy={isShowingStaleDay}
              className={`w-full px-4 py-3 text-left rounded-lg! border! transition-all duration-200 flex items-center justify-between ${
                isShowingStaleDay ? 'cursor-not-allowed' : 'cursor-pointer'
              } ${
                isSelected
                  ? 'border-(--neutral-black-NB1000)!'
                  : 'border-(--neutral-white-NW800)! hover:border-(--neutral-black-NB400)!'
              }`}
            >
              <span
                className={`[font-size:var(--fs-l5)]!  ${isSelected ? 'text-(--neutral-black-NB1000)!' : 'text-(--neutral-white-NW600)'}`}
              >
                {formatSlotStartTime(slot.start)}
              </span>
              {isSelected && <Check className="w-5 h-5 text-(--neutral-black-NB1000)!" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};
